export interface KeywordData {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface PageGapAnalysis {
  targetUrl: string;
  domain: string;
  path: string;
  totalKeywords: number;
  totalClicks: number;
  totalImpressions: number;
  averagePosition: number;
  potentialAnchors: PotentialAnchor[];
}

export interface PotentialAnchor {
  keyword: string;
  clicks: number;
  impressions: number;
  position: number;
  ctr: number;
  priority: 'high' | 'medium' | 'low';
  reason: string;
}

export interface InternalLinkGapResult {
  totalPages: number;
  totalPotentialAnchors: number;
  pageAnalyses: PageGapAnalysis[];
  summary: {
    highPriorityOpportunities: number;
    mediumPriorityOpportunities: number;
    lowPriorityOpportunities: number;
  };
}

/**
 * Analyzes Search Console data to identify internal link gap opportunities
 * Groups keywords by target URL and identifies potential anchor text opportunities
 */
export function analyzeInternalLinkGaps(data: any[]): InternalLinkGapResult {
  if (!data || data.length === 0) {
    return {
      totalPages: 0,
      totalPotentialAnchors: 0,
      pageAnalyses: [],
      summary: {
        highPriorityOpportunities: 0,
        mediumPriorityOpportunities: 0,
        lowPriorityOpportunities: 0,
      }
    };
  }

  // Group keywords by target URL (page)
  const pageGroups = new Map<string, KeywordData[]>();
  
  data.forEach(row => {
    const page = row.page || row.url || 'Unknown URL';
    const keyword: KeywordData = {
      query: row.query || row.keyword || '',
      clicks: parseInt(row.clicks) || 0,
      impressions: parseInt(row.impressions) || 0,
      ctr: parseFloat(row.ctr) || 0,
      position: parseFloat(row.position) || 0
    };

    if (!pageGroups.has(page)) {
      pageGroups.set(page, []);
    }
    pageGroups.get(page)!.push(keyword);
  });

  // Analyze each page for internal link opportunities
  const pageAnalyses: PageGapAnalysis[] = [];
  let totalPotentialAnchors = 0;
  let highPriorityCount = 0;
  let mediumPriorityCount = 0;
  let lowPriorityCount = 0;

  pageGroups.forEach((keywords, targetUrl) => {
    // Extract domain and path
    let domain = 'Unknown Domain';
    let path = targetUrl;
    
    try {
      const url = new URL(targetUrl.startsWith('http') ? targetUrl : `https://${targetUrl}`);
      domain = url.hostname;
      path = url.pathname;
    } catch (e) {
      // Handle relative URLs or malformed URLs
      if (targetUrl.includes('/')) {
        const parts = targetUrl.split('/');
        domain = parts[0];
        path = '/' + parts.slice(1).join('/');
      }
    }

    // Calculate page metrics
    const totalClicks = keywords.reduce((sum, k) => sum + k.clicks, 0);
    const totalImpressions = keywords.reduce((sum, k) => sum + k.impressions, 0);
    const averagePosition = keywords.reduce((sum, k, _, arr) => sum + k.position / arr.length, 0);

    // Generate potential anchor text suggestions
    const potentialAnchors = keywords
      .sort((a, b) => {
        // Sort by clicks first, then by position (lower is better)
        if (b.clicks !== a.clicks) return b.clicks - a.clicks;
        return a.position - b.position;
      })
      .map(keyword => {
        const priority = determinePriority(keyword);
        const reason = getPriorityReason(keyword);
        
        return {
          keyword: keyword.query,
          clicks: keyword.clicks,
          impressions: keyword.impressions,
          position: keyword.position,
          ctr: keyword.ctr,
          priority,
          reason
        } as PotentialAnchor;
      });

    // Count priorities
    potentialAnchors.forEach(anchor => {
      if (anchor.priority === 'high') highPriorityCount++;
      else if (anchor.priority === 'medium') mediumPriorityCount++;
      else lowPriorityCount++;
    });

    totalPotentialAnchors += potentialAnchors.length;

    pageAnalyses.push({
      targetUrl,
      domain,
      path,
      totalKeywords: keywords.length,
      totalClicks,
      totalImpressions,
      averagePosition,
      potentialAnchors
    });
  });

  // Sort pages by total clicks (most valuable first)
  pageAnalyses.sort((a, b) => b.totalClicks - a.totalClicks);

  return {
    totalPages: pageGroups.size,
    totalPotentialAnchors,
    pageAnalyses,
    summary: {
      highPriorityOpportunities: highPriorityCount,
      mediumPriorityOpportunities: mediumPriorityCount,
      lowPriorityOpportunities: lowPriorityCount
    }
  };
}

/**
 * Determines priority level for a potential anchor text
 */
function determinePriority(keyword: KeywordData): 'high' | 'medium' | 'low' {
  // High priority: Good traffic + decent position (room for improvement)
  if (keyword.clicks >= 5 && keyword.position > 3 && keyword.position <= 10) {
    return 'high';
  }
  
  // High priority: High impressions but low clicks (poor CTR, might need better anchor text)
  if (keyword.impressions >= 100 && keyword.clicks < 5 && keyword.position <= 15) {
    return 'high';
  }

  // Medium priority: Some traffic or good position
  if (keyword.clicks >= 1 || (keyword.position <= 5 && keyword.impressions >= 20)) {
    return 'medium';
  }

  // Low priority: Everything else
  return 'low';
}

/**
 * Gets explanation for why anchor text is prioritized
 */
function getPriorityReason(keyword: KeywordData): string {
  if (keyword.clicks >= 5 && keyword.position > 3 && keyword.position <= 10) {
    return `Good traffic (${keyword.clicks} clicks) but ranking at position ${keyword.position.toFixed(1)} - internal links could boost rankings`;
  }
  
  if (keyword.impressions >= 100 && keyword.clicks < 5 && keyword.position <= 15) {
    return `High visibility (${keyword.impressions} impressions) but low CTR - better internal anchor text could improve relevance signals`;
  }

  if (keyword.clicks >= 1) {
    return `Already driving ${keyword.clicks} clicks - internal links could amplify performance`;
  }

  if (keyword.position <= 5 && keyword.impressions >= 20) {
    return `Strong position ${keyword.position.toFixed(1)} with ${keyword.impressions} impressions - maximize opportunity with internal links`;
  }

  return `Potential anchor text opportunity at position ${keyword.position.toFixed(1)}`;
}

/**
 * Filters gap analysis results by priority
 */
export function filterGapAnalysisByPriority(
  result: InternalLinkGapResult, 
  priorities: Array<'high' | 'medium' | 'low'>
): InternalLinkGapResult {
  const filteredPageAnalyses = result.pageAnalyses.map(pageAnalysis => ({
    ...pageAnalysis,
    potentialAnchors: pageAnalysis.potentialAnchors.filter(anchor => 
      priorities.includes(anchor.priority)
    )
  })).filter(pageAnalysis => pageAnalysis.potentialAnchors.length > 0);

  const filteredTotalAnchors = filteredPageAnalyses.reduce(
    (sum, page) => sum + page.potentialAnchors.length, 
    0
  );

  return {
    ...result,
    pageAnalyses: filteredPageAnalyses,
    totalPotentialAnchors: filteredTotalAnchors
  };
}